import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Box,
  Typography,
  Container,
  Paper,
  Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Call the login function and await its response
      const response = await login(email, password);
      
      // Log successful login
      console.log('Login successful:', response);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      // More detailed error handling
      console.error('Login error:', err);
      
      if (err.response && err.response.data && err.response.data.message) {
        // Use the error message from the server if available
        setError(err.response.data.message);
      } else if (err.message) {
        // Use the error message from the Error object
        setError(err.message);
      } else {
        // Default error message
        setError('Failed to log in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle the forgot password navigation
  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };
  
  // Then in the return statement, add a Forgot Password button
  // Find the section after the password TextField and before the Sign In button
  // Around line 80-90, add:
  
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
    <Button 
      onClick={handleForgotPassword}
      sx={{ 
        textTransform: 'none',
        color: 'primary.main',
        '&:hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline',
        }
      }}
    >
      Forgot Password?
    </Button>
  </Box>
  return (
    <>
      <Seo title="Sign In" description="Sign in to your MoodCraft account." canonicalPath="/login" noIndex />
      <Container component="main" maxWidth="xs">
        <Paper elevation={3} sx={{ mt: 8, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ fontFamily: '"Lato", sans-serif' }}>
            Sign in to MoodCraft
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link to="/forgot-password">
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link to="/register">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <Box mt={4} textAlign="center">
          <Button variant="text" color="primary" component={Link} to="/">
            Back to Home
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default Login;
